"use client"

import type React from "react"

import { useState } from "react"
import { Card, Typography, Box, Chip } from "@mui/material"
import type { SegmentPart } from "./lib/types"
import type { DragData } from "./lib/hooks/use-drag-drop"

interface SegmentPartItemProps {
  part: SegmentPart
  parentWorkOrderName: string
  segmentName?: string
  partNumber?: number
  onDragStart: (e: React.DragEvent, data: DragData) => void
  onDragEnd: (e: React.DragEvent) => void
  // Work order fields for display in technician card
  workOrderNumber?: string
  invoiceNumber?: string
  customerName?: string
  customerNumber?: string
  unitNumber?: string
  unitModel?: string
  vinNumber?: string
  myReference?: string
  woStatusId?: number
  woDescription?: string
  woPriorityId?: number
}

export function SegmentPartItem({ 
  part, 
  parentWorkOrderName, 
  segmentName, 
  partNumber, 
  onDragStart, 
  onDragEnd,
  workOrderNumber,
  invoiceNumber,
  customerName,
  customerNumber,
  unitNumber,
  unitModel,
  vinNumber,
  myReference,
  woStatusId,
  woDescription,
  woPriorityId,
}: SegmentPartItemProps) {
  const formatHours = (hours: number) => {
    const totalMinutes = Math.round(hours * 60)
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return `${h}.${m.toString().padStart(2, "0")}`
  }

  const handleDragStart = (e: React.DragEvent) => {
    if (part.isUsed) {
      e.preventDefault()
      return
    }
    onDragStart(e, {
      type: "segment-part",
      id: part.id,
      hours: part.hours,
      workOrderId: part.parentWorkOrderId,
      segmentId: part.parentSegmentId, // CRITICAL: Include parent segmentId for saving ScheduleItem
      segmentName: segmentName || part.segmentName, // Include segment name when moving segment part to tech card
      // Include work order details for display in technician card
      workOrderNumber,
      invoiceNumber,
      customerName,
      customerNumber,
      unitNumber,
      unitModel,
      vinNumber,
      myReference,
      woStatusId,
      woDescription,
      woPriorityId,
    })
  }

  const [isDragging, setIsDragging] = useState(false)

  const handleDragStartLocal = (e: React.DragEvent) => {
    if (part.isUsed) {
      e.preventDefault()
      return
    }
    setIsDragging(true)
    handleDragStart(e)
  }

  const handleDragEndLocal = (e: React.DragEvent) => {
    setIsDragging(false)
    onDragEnd(e)
  }

  return (
    <Card
      elevation={isDragging ? 8 : part.isUsed ? 0 : 3}
      draggable={!part.isUsed}
      onDragStart={handleDragStartLocal}
      onDragEnd={handleDragEndLocal}
      sx={{
        p: 2,
        mb: 1.5,
        bgcolor: "background.paper",
        cursor: part.isUsed ? "not-allowed" : "grab",
        "&:active": { cursor: part.isUsed ? "not-allowed" : "grabbing" },
        border: part.isUsed ? "2px dashed" : "2px solid",
        borderColor: part.isUsed ? "action.disabled" : "primary.main",
        borderRadius: 2,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: part.isUsed ? 0.5 : isDragging ? 0.6 : 1,
        transform: isDragging ? "rotate(2deg) scale(0.95)" : "scale(1)",
        boxShadow: isDragging
          ? "0 12px 32px rgba(25, 118, 210, 0.3)"
          : part.isUsed
            ? "none"
            : "0 2px 8px rgba(25, 118, 210, 0.15)",
        // Improved hover style: subtle background tint, enhanced border, and elevation
        "&:hover": part.isUsed
          ? {}
          : {
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(25, 118, 210, 0.12)"
                  : "rgba(25, 118, 210, 0.06)",
              borderColor: "primary.dark",
              borderWidth: "2px",
              transform: "translateY(-3px) scale(1.02)",
              boxShadow: "0 8px 24px rgba(25, 118, 210, 0.25)",
              "& .MuiTypography-root": {
                color: "text.primary", // Keep text readable
              },
              "& .MuiChip-root": {
                bgcolor: "primary.main",
                color: "primary.contrastText",
                transform: "scale(1.05)",
                transition: "all 0.2s",
              },
            },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          display: "block",
          mb: 0.5,
          color: part.isUsed ? "text.disabled" : "text.primary",
          fontSize: "0.8rem",
          lineHeight: 1.3,
        }}
      >
        {segmentName || "Segment Part"}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: part.isUsed ? "text.disabled" : "primary.main",
            fontSize: "0.75rem",
          }}
        >
          {formatHours(part.hours)}h
        </Typography>
        <Chip
          key={part.id}
          label={part.isUsed ? "Used" : partNumber ? `Part ${partNumber}` : "Part"}
          size="small"
          variant={part.isUsed ? "outlined" : "filled"}
          color={part.isUsed ? "default" : "primary"}
          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          data-part-id={part.id}
        />
        </Box>
      </Card>
    );
  }
